import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * UserGroupDrawer — slide-out drawer for Add / Edit user group.
 * Includes a searchable user picker for user assignment.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   group: object | null,           // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (group: object) => void,
 *   users: Array<{ user_id: number, username: string, display_name: string, user_role_name: string, user_role_weight: number }>,
 *   myRoleWeight: number,
 *   iAmHigherAuthority: (targetWeight: number) => boolean
 * }} props
 */
const UserGroupDrawer = ({ isOpen, onClose, group, onSave, onDelete, users, myRoleWeight, iAmHigherAuthority, canManageGroups = true }) => {
  const isEditMode = !!group;
  const drawerRef = useRef(null);

  // Form state
  const [groupName, setGroupName] = useState('');
  const [capcode, setCapcode] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form on open/group change
  useEffect(() => {
    if (isOpen && group) {
      setGroupName(group.group_name || '');
      setCapcode(group.capcode || '');
      setSelectedUserIds(
        (group.users || []).map((u) => u.user_id?.toString() || u.toString())
      );
      setUserSearch('');
      setShowSelectedOnly(false);
    } else if (isOpen && !group) {
      setGroupName('');
      setCapcode('');
      setSelectedUserIds([]);
      setUserSearch('');
      setShowSelectedOnly(false);
    }
  }, [isOpen, group]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Filter users by search and "show selected" toggle
  const filteredUsers = useMemo(() => {
    let list = users || [];
    if (showSelectedOnly) {
      list = list.filter((u) => selectedUserIds.includes(u.user_id.toString()));
    }
    if (userSearch.trim()) {
      const s = userSearch.toLowerCase();
      list = list.filter(
        (u) =>
          u.username?.toLowerCase().includes(s) ||
          u.display_name?.toLowerCase().includes(s) ||
          u.user_role_name?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [users, userSearch, showSelectedOnly, selectedUserIds]);

  // Authority guard — the current (impersonated) role must outrank the
  // target user to add or remove them from the group. PalCare Admin
  // (weight >= 1000) always passes via iAmHigherAuthority's short-circuit.
  const canManageUser = useCallback(
    (u) => iAmHigherAuthority(u.user_role_weight, myRoleWeight),
    [iAmHigherAuthority, myRoleWeight]
  );

  const handleUserToggle = useCallback((userId) => {
    const uStr = userId.toString();
    // Hard guard — even if the checkbox is somehow clicked, don't mutate
    // membership for users the current role isn't allowed to manage.
    const target = (users || []).find((u) => u.user_id?.toString() === uStr);
    if (target && !canManageUser(target)) return;
    setSelectedUserIds((prev) =>
      prev.includes(uStr) ? prev.filter((id) => id !== uStr) : [...prev, uStr]
    );
  }, [users, canManageUser]);

  const handleSelectAllUsers = useCallback(() => {
    // Only select users the current user has authority over
    const selectableIds = filteredUsers
      .filter(canManageUser)
      .map((u) => u.user_id.toString());
    setSelectedUserIds((prev) => [...new Set([...prev, ...selectableIds])]);
  }, [filteredUsers, canManageUser]);

  const handleClearUsers = useCallback(() => {
    // Only clear users the current (impersonated) role is allowed to manage.
    // Higher-role users the current role can't remove must stay selected so
    // the save payload preserves their existing membership.
    setSelectedUserIds((prev) =>
      prev.filter((uStr) => {
        const u = (users || []).find((usr) => usr.user_id?.toString() === uStr);
        // If the user isn't in the list (edge case) or is out of authority, keep them selected.
        return !u || !canManageUser(u);
      })
    );
  }, [users, canManageUser]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const formData = {
        group_id: group?.group_id || null,
        group_name: groupName.trim(),
        capcode: capcode.trim(),
        user_ids: selectedUserIds.map((id) => parseInt(id, 10)),
        // cognito_ids will be resolved by the orchestrator from the users data
      };

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [groupName, capcode, selectedUserIds, group, onSave]
  );

  /* Hands off to the page's typed confirmation — see UserDrawer for why the
     inline footer confirmation was removed. */
  const handleDeleteClick = useCallback(() => {
    if (group && onDelete) onDelete(group);
  }, [group, onDelete]);

  const isFormValid = groupName.trim();

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="up-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`up-drawer up-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit User Group' : 'New User Group'}
      >
        {/* Header */}
        <div className="up-drawer__header">
          <h2 className="up-drawer__title">
            {isEditMode ? 'Edit User Group' : 'New User Group'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="up-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="up-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="up-form">
          <div className="up-form__body">
            {/* Group Name */}
            <div>
              <label htmlFor="drawer-group-name" className="up-field__label">
                Group Name <span className="up-field__required">*</span>
              </label>
              <input
                type="text"
                id="drawer-group-name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                disabled={!canManageGroups}
                className="up-field__input"
                placeholder="Enter group name"
              />
              {!canManageGroups ? (
                <p className="up-field__hint">Your role cannot edit the group name.</p>
              ) : null}
            </div>

            {/* Pager Channel (Capcode) */}
            <div>
              <label htmlFor="drawer-group-capcode" className="up-field__label">
                Pager Channel (Capcode)
              </label>
              <input
                type="text"
                id="drawer-group-capcode"
                value={capcode}
                onChange={(e) => setCapcode(e.target.value)}
                disabled={!canManageGroups}
                className="up-field__input"
                placeholder="Optional capcode"
              />
            </div>

            {/* User Picker */}
            <div className="up-user-picker">
              <div className="up-user-picker__header">
                <p className="up-field__label" style={{ marginBottom: 0 }}>
                  Users
                  <span className="up-user-picker__count">
                    ({selectedUserIds.length} selected)
                  </span>
                </p>
              </div>

              {/* Search */}
              <div className="up-user-picker__search">
                <i className="fe fe-search up-user-picker__search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="up-user-picker__search-input"
                />
              </div>

              {/* Select All / Clear / Show Selected */}
              <div className="up-user-picker__actions">
                <button
                  type="button"
                  onClick={handleSelectAllUsers}
                  className="up-user-picker__action-btn"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearUsers}
                  className="up-user-picker__action-btn"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setShowSelectedOnly((prev) => !prev)}
                  className={`up-user-picker__action-btn${showSelectedOnly ? ' up-user-picker__action-btn--active' : ''}`}
                >
                  Show Selected
                </button>
              </div>

              {/* User list */}
              <div className="up-user-picker__list">
                {filteredUsers.length === 0 ? (
                  <p className="up-user-picker__empty">
                    {showSelectedOnly ? 'No selections.' : 'No users found'}
                  </p>
                ) : (
                  filteredUsers.map((u) => {
                    const isChecked = selectedUserIds.includes(u.user_id.toString());
                    const canManage = canManageUser(u);
                    return (
                      <label
                        key={u.user_id}
                        className={`up-user-picker__item ${isChecked ? 'is-selected' : ''}${canManage ? '' : ' is-disabled'}`}
                        title={canManage ? undefined : 'Your role cannot add or remove this user.'}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleUserToggle(u.user_id)}
                          disabled={!canManage}
                          className="up-checkbox"
                        />
                        <div className="up-user-picker__item-info">
                          <span className="up-user-picker__item-name">{u.display_name}</span>
                          <span className="up-user-picker__item-role">
                            {u.username}{u.user_role_name ? ` - ${u.user_role_name}` : ''}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer with actions */}
          <div className="up-form__footer">
            {/* Delete button (edit mode only) — opens the page's typed
                confirmation rather than confirming here. */}
            {isEditMode && canManageGroups ? (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="up-btn up-btn--danger"
              >
                <i className="fe fe-trash-2" aria-hidden="true" />
                Delete
              </button>
            ) : null}

            {/* Spacer pushes Cancel/Save right */}
            <div style={{ flex: 1 }} />

                <button
                  type="button"
                  onClick={onClose}
                  className="up-btn up-btn--cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || saving}
                  className="up-btn up-btn--primary"
                >
                  {saving ? (
                    <span className="up-saving-text">
                      <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </span>
              ) : (
                isEditMode ? 'Save Changes' : 'Create Group'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UserGroupDrawer;
